"use client"

import React, {useState} from "react"
import {useRouter} from "next/navigation"
import {toast} from "sonner"
import {MessagesSquare} from "lucide-react"
import {Button} from "@/components/ui/button"
import {Checkbox} from "@/components/ui/checkbox"
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from "@/components/ui/dialog"
import {Label} from "@/components/ui/label"
import {ResizableTextarea} from "@/app/components/ResizableTextarea"
import {FileDropOverlay} from "@/app/components/FileDropOverlay"
import {LinkPreviewList} from "@/app/components/editor/link-preview/LinkPreviewList"
import {
  OptimisticFilePreviewList,
  useOptimisticAttachments,
} from "@/app/components/projects/creation/OptimisticAttachmentsInput"
import {useBoundStore} from "@/hooks/queries/stores/use-bound-store"
import {chatPath} from "@/app/paths"
import {writeScopingInitialBrief} from "@/app/components/scoping/scopingInitialBrief"
import {WorkspaceChatDetails, WorkspaceChatMode} from "@/pb/api/devplan/types/workspace_chats"

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  companyId: number
}

// Pre-chat composer. On Next: creates a workspace chat, stashes the brief
// under sessionStorage, redirects to /chats/{id}. The workspace_chat
// initializer reads the brief and auto-sends as the first user message; the
// agent emits propose_projects in response.
export function ScopingIntakeDialog({open, onOpenChange, companyId}: Props) {
  const router = useRouter()
  const {createWorkspaceChat} = useBoundStore()
  const [description, setDescription] = useState("")
  const [trackOnly, setTrackOnly] = useState(false)
  const [composerKey, setComposerKey] = useState(0)
  const [creating, setCreating] = useState(false)

  const optimistic = useOptimisticAttachments({
    companyId,
    description,
    disabled: creating,
  })

  const canSubmit = !!description.trim() && !optimistic.uploadsInFlight && !creating

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setComposerKey(k => k + 1)
      setDescription("")
      setTrackOnly(false)
    }
    onOpenChange(next)
  }

  const handleNext = async () => {
    if (!canSubmit) return
    setCreating(true)
    try {
      const mode = trackOnly
        ? WorkspaceChatMode.WORKSPACE_CHAT_MODE_TRACK_ONLY
        : WorkspaceChatMode.WORKSPACE_CHAT_MODE_SCOPING_FIRST
      const {chat} = await createWorkspaceChat(companyId, WorkspaceChatDetails.create({mode}))
      const payload = optimistic.buildPayload()
      const trimmed = description.trim()
      const briefMessage = buildInitialBriefMessage(trimmed, trackOnly)
      writeScopingInitialBrief(chat.id, {
        description: briefMessage,
        imageUrls: payload.imageUrls,
        attachmentIds: payload.attachmentIds,
        attachments: optimistic.attachments,
        issueAttachments: payload.issueAttachments,
      })
      onOpenChange(false)
      setComposerKey(k => k + 1)
      setDescription("")
      setTrackOnly(false)
      router.push(chatPath(companyId, chat.id))
    } catch (e) {
      toast.error(`Could not start scoping chat: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setCreating(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="space-y-3 shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-indigo-500/10 text-indigo-500 dark:bg-indigo-500/20 dark:text-indigo-300">
              <MessagesSquare className="h-5 w-5"/>
            </div>
            <div className="space-y-1">
              <DialogTitle>Scope/track work</DialogTitle>
              <p className="text-xs text-muted-foreground">
                Describe what you want to ship, drop in supporting docs and issue links. The agent will propose project candidates you can refine and create.
              </p>
            </div>
          </div>
          <DialogDescription>
            On the next step the agent will propose projects and help evaluate them before you create anything.
          </DialogDescription>
        </DialogHeader>
        <div key={composerKey} className="min-h-0 flex-1 overflow-y-auto mt-4">
          <div className="space-y-4 relative" {...optimistic.dragHandlers}>
            <FileDropOverlay visible={optimistic.dragActive} className="rounded-lg"/>
            <div className="space-y-2">
              <p className="text-sm font-medium">What&apos;s the brief?</p>
              <div className="relative">
                <ResizableTextarea
                  rows={5}
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  onPaste={optimistic.handlePaste}
                  placeholder="Outline what's coming in — scope, customer ask, related work. Paste Jira/Linear links and drop docs or screenshots as supporting context."
                  className="pr-10 pb-10"
                  disabled={creating}
                />
                <div className="absolute bottom-2 right-2">{optimistic.uploadTrigger}</div>
              </div>
            </div>
            <OptimisticFilePreviewList
              pendingFiles={optimistic.pendingFiles}
              imageUrls={optimistic.imageUrls}
              attachments={optimistic.attachments}
              onRemovePending={optimistic.removePending}
              onRemoveImage={optimistic.removeImage}
              onRemoveAttachment={optimistic.removeAttachment}
            />
            {optimistic.detectedLinks.length > 0 && (
              <LinkPreviewList
                companyId={companyId}
                links={optimistic.detectedLinks}
                onRemove={optimistic.handleRemoveLink}
                onPreviewLoaded={optimistic.handlePreviewLoaded}
              />
            )}
            <div className="flex items-start gap-3 rounded-md border bg-muted/30 p-3">
              <Checkbox
                id="workspace-intake-track-only"
                checked={trackOnly}
                onCheckedChange={value => setTrackOnly(value === true)}
                disabled={creating}
                className="mt-0.5"
              />
              <div className="space-y-1">
                <Label htmlFor="workspace-intake-track-only" className="text-sm font-medium">
                  Track only (no discovery)
                </Label>
                <p className="text-xs text-muted-foreground">
                  Skip clarifying questions and ask the agent to propose projects right away.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4 flex gap-3 shrink-0 flex-col-reverse sm:flex-row sm:items-center sm:justify-between">
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={creating}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleNext()}
            disabled={!canSubmit}
            className="bg-indigo-500 text-white hover:bg-indigo-500/90 dark:bg-indigo-400 dark:hover:bg-indigo-400/90"
          >
            {creating ? "Starting…" : optimistic.uploadsInFlight ? "Waiting for uploads…" : "Next →"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function buildInitialBriefMessage(trimmedDescription: string, trackOnly: boolean): string {
  if (trackOnly) {
    return trimmedDescription
      ? `Propose projects for:\n${trimmedDescription}`
      : "Propose projects for:"
  }
  return trimmedDescription
}
